import { useCallback, useEffect, useRef, useState } from 'react'
import {
  getCoursePublishStateService,
  publishCourseService,
  publishCourseChangesService,
  unpublishCourseService,
} from 'src/api/course'
import {
  discardCardChangesService,
  publishCardService,
  unpublishCardService,
} from 'src/api/card'

/**
 * Where a single page sits in the two-state model. A page is never both draft and edited:
 * grey "Draft" means nobody can see it, orange means what people see is out of date.
 */
export const CardPublishState = {
  DRAFT: 0,
  PUBLISHED: 1,
  PUBLISHED_WITH_CHANGES: 2,
}

/**
 * Publish state of a course and its pages, plus the actions that change it.
 *
 * Every action refetches rather than patching local state: publishing a page can change the
 * course-level "anything pending?" answer, and the two must not drift apart.
 */
export function useCoursePublishState(courseId) {
  const [state, setState] = useState(null)
  const [loading, setLoading] = useState(false)
  // Bumped per fetch so a reply for the course you just left cannot overwrite the one you opened.
  const requestId = useRef(0)

  const refresh = useCallback(async () => {
    if (!courseId) {
      requestId.current += 1
      setState(null)
      return null
    }

    const request = ++requestId.current
    setLoading(true)

    try {
      const response = await getCoursePublishStateService(courseId)
      // The API wraps payloads in { data, statusCode } — every saga reads response.data. Fall
      // back to the raw body so this keeps working if that envelope ever goes away.
      const result = response?.data ?? response

      if (request === requestId.current) {
        setState(result)
      }

      return result
    } catch (error) {
      // Leave state null rather than half-set: callers treat "not loaded" as unknown, which is
      // very different from "draft".
      if (request === requestId.current) {
        setState(null)
      }

      return null
    } finally {
      if (request === requestId.current) {
        setLoading(false)
      }
    }
  }, [courseId])

  // Open a second course and its badges must start unknown. Carrying the previous course's state
  // over would briefly label its pages with answers belonging to a course you already left.
  useEffect(() => {
    setState(null)
  }, [courseId])

  useEffect(() => {
    refresh()
  }, [refresh])

  const run = useCallback(
    async action => {
      await action()
      return refresh()
    },
    [refresh],
  )

  return {
    loading,
    // Never guess. A course whose state has not loaded is unknown, and rendering unknown as
    // "Draft" is how every course in the editor ended up wearing a Draft badge.
    isLoaded: state !== null,
    isPublished: state?.isPublished === true,
    // A course that has never gone live is the only case where Preview shows everything.
    hasEverBeenPublished: state?.hasEverBeenPublished ?? false,
    pages: state?.pages ?? [],
    pendingPages: (state?.pages ?? []).filter(
      page => page.state !== CardPublishState.PUBLISHED,
    ),
    refresh,
    publishCourse: () => run(() => publishCourseService(courseId)),
    unpublishCourse: () => run(() => unpublishCourseService(courseId)),
    publishChanges: cardIds =>
      run(() => publishCourseChangesService(courseId, cardIds)),
    publishPage: cardId => run(() => publishCardService(cardId)),
    unpublishPage: cardId => run(() => unpublishCardService(cardId)),
    discardPageChanges: cardId => run(() => discardCardChangesService(cardId)),
  }
}

export default useCoursePublishState
